import { useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import Seo from "@/components/Seo";
import api from "@/lib/api";
import { PageHero, CtaBand, splitRows, CARD_MAX_W } from "@/components/blocks";
import { ArticleCard } from "@/components/ArticleCard";
import { categoryFromSlug } from "@/data/techAdvice";
import { breadcrumbJsonLd } from "@/lib/seo";

export default function TechAdviceCategory() {
  const { categorySlug } = useParams();
  const categoryName = categoryFromSlug(categorySlug);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!categoryName) return;
    setLoading(true);
    api.get("/articles", { params: { category: categoryName, limit: 50 } })
      .then(({ data }) => setArticles(data.items))
      .finally(() => setLoading(false));
  }, [categoryName]);

  if (!categoryName) return <Navigate to="/technology-advice" replace />;

  const [mainArticles, lastRow] = splitRows(articles, 3);
  const crumbs = [{ label: "Technology Advice", to: "/technology-advice" }, { label: categoryName }];

  return (
    <>
      <Seo
        title={`${categoryName} | Technology Advice | CTS`}
        description={`Practical CTS guidance on ${categoryName.toLowerCase()}.`}
        jsonLd={breadcrumbJsonLd(crumbs)}
      />
      <PageHero
        eyebrow="TECHNOLOGY ADVICE"
        title={categoryName}
        intro={`Practical CTS guidance and articles on ${categoryName.toLowerCase()}.`}
        crumbs={crumbs}
      />
      <section className="section-pad" data-testid="tech-advice-category-articles">
        <div className="container-site">
          {loading ? null : articles.length === 0 ? (
            <div className="card-base p-10 text-center max-w-2xl" data-testid="tech-advice-category-empty">
              <p className="text-[15px] leading-relaxed text-cts-charcoal">
                We haven't published an article in this category yet. Check back soon, or browse our other
                Technology Advice topics.
              </p>
            </div>
          ) : (
            <>
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {mainArticles.map((a, i) => <ArticleCard key={a.slug} article={a} i={i} />)}
              </div>
              {lastRow.length > 0 && (
                <div className="flex flex-wrap justify-center gap-6 mt-6">
                  {lastRow.map((a, i) => (
                    <div key={a.slug} className="w-full sm:w-[calc(50%-12px)]" style={{ maxWidth: CARD_MAX_W[3] }}>
                      <ArticleCard article={a} i={mainArticles.length + i} />
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>
      <CtaBand />
    </>
  );
}
